import React, { useEffect, useState, useRef } from 'react';
interface CountdownTimerProps {
  className?: string;
  boxClassName?: string;
  textClassName?: string;
  labelClassName?: string;
}
function getEndTime(): number {
  try {
    const saved = localStorage.getItem('megapack_countdown_end');
    if (saved) {
      const t = parseInt(saved, 10);
      if (t > Date.now()) return t;
    }
    const t = Date.now() + 23 * 3600000 + 58 * 60000 + 33000;
    localStorage.setItem('megapack_countdown_end', t.toString());
    return t;
  } catch {
    return Date.now() + 23 * 3600000 + 58 * 60000 + 33000;
  }
}
function computeDisplay(endTime: number) {
  const d = endTime - Date.now();
  if (d <= 0) return {
    h: '00',
    m: '00',
    s: '00'
  };
  return {
    h: String(Math.floor(d / 3600000 % 24)).padStart(2, '0'),
    m: String(Math.floor(d / 60000 % 60)).padStart(2, '0'),
    s: String(Math.floor(d / 1000 % 60)).padStart(2, '0')
  };
}
export function CountdownTimer({
  className = 'flex gap-4 justify-center',
  boxClassName = 'bg-brand-dark border border-brand-border rounded-xl p-4 min-w-[90px] flex flex-col items-center',
  textClassName = 'text-5xl font-heading font-bold text-brand-accent',
  labelClassName = 'text-xs uppercase tracking-widest text-white/50 mt-2 font-badge font-bold'
}: CountdownTimerProps) {
  const endTimeRef = useRef<number>(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const intervalRef = useRef<number | null>(null);
  const [display, setDisplay] = useState({
    h: '00',
    m: '00',
    s: '00'
  });
  useEffect(() => {
    const init = () => {
      endTimeRef.current = getEndTime();
      setDisplay(computeDisplay(endTimeRef.current));
    };
    const w = window as any;
    const idle = w.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 200));
    const handle = idle(init);
    return () => {
      if (w.cancelIdleCallback && typeof handle === 'number') {
        try {
          w.cancelIdleCallback(handle);
        } catch {}
      }
    };
  }, []);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const start = () => {
      if (intervalRef.current != null) return;
      if (endTimeRef.current) setDisplay(computeDisplay(endTimeRef.current));
      intervalRef.current = window.setInterval(() => {
        if (!endTimeRef.current) return;
        setDisplay(computeDisplay(endTimeRef.current));
      }, 1000);
    };
    const stop = () => {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    if (typeof IntersectionObserver === 'undefined') {
      start();
      return stop;
    }
    const io = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) start();else stop();
      }
    }, {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.1
    });
    io.observe(el);
    const onVisibility = () => {
      if (document.hidden) stop();else if (el.getBoundingClientRect().top < window.innerHeight) start();
    };
    document.addEventListener('visibilitychange', onVisibility, {
      passive: true
    });
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);
  return <div ref={rootRef} className={className} data-id="element-53">
      <div className={boxClassName} data-id="element-54">
        <span className={textClassName} data-id="element-55">{display.h}</span>
        <span className={labelClassName} data-id="element-56">hs</span>
      </div>
      <div className="text-5xl font-bold text-white/30 self-start mt-3" data-id="element-57">:</div>
      <div className={boxClassName} data-id="element-58">
        <span className={textClassName} data-id="element-59">{display.m}</span>
        <span className={labelClassName} data-id="element-60">min</span>
      </div>
      <div className="text-5xl font-bold text-white/30 self-start mt-3" data-id="element-61">:</div>
      <div className={boxClassName} data-id="element-62">
        <span className={textClassName} data-id="element-63">{display.s}</span>
        <span className={labelClassName} data-id="element-64">sec</span>
      </div>
    </div>;
}